import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * The sequence flow canvas.
 *
 * A sequence is a list of steps, each carrying the delay that precedes it. That
 * is the shape the API wants and the shape the scheduler reads, but it is not
 * how anyone thinks about a follow-up: people picture an email, then a wait,
 * then another email. So the delay is drawn on the connector *above* its step
 * rather than inside it — one row of data, rendered as two things on the rail.
 *
 * Reordering therefore moves a step together with its own delay. Dragging the
 * second email above the first takes "wait 3 days" with it, which is what the
 * stored value has always meant: time since the previous send, not a position
 * on a calendar.
 */

export type Condition = "always" | "no_reply" | "no_open" | "no_click";

export interface Step {
  /** Client-side identity so a removed step does not reshuffle React state. */
  uid: string;
  templateId: string;
  delayHours: number;
  condition: Condition;
  /**
   * Which unit the delay is being edited in. UI-only: the API is always sent
   * hours. Kept per step so a field reading "3 days" does not flip to "72
   * hours" the moment it is touched.
   */
  unit: "hours" | "days";
}

export interface TemplateOption {
  id: string;
  name: string;
  subject?: string;
}

const CONDITION_LABEL: Record<Condition, string> = {
  always: "Always send",
  no_reply: "Only if no reply",
  no_open: "Only if not opened",
  no_click: "Only if not clicked",
};

/** Short form for the chip on a card, where the full sentence does not fit. */
const CONDITION_CHIP: Record<Condition, string> = {
  always: "Always",
  no_reply: "No reply",
  no_open: "No open",
  no_click: "No click",
};

function newStep(condition: Condition = "no_reply", delayHours = 72): Step {
  return {
    uid: crypto.randomUUID(),
    templateId: "",
    delayHours,
    // Whole days are how follow-up is actually planned; hours are the exception.
    unit: delayHours > 0 && delayHours % 24 === 0 ? "days" : "hours",
    condition,
  };
}

/**
 * API shape to editor shape. The editor adds a client id and a display unit;
 * neither is sent back.
 */
export function hydrateSteps(
  rows: { templateId: string; delayHours: number; condition: Condition }[],
): Step[] {
  return rows.map((r) => ({
    uid: crypto.randomUUID(),
    templateId: r.templateId,
    delayHours: r.delayHours,
    condition: r.condition,
    unit: r.delayHours > 0 && r.delayHours % 24 === 0 ? "days" : "hours",
  }));
}

/** The first step is the entry email: it has no predecessor to wait for. */
export function firstStep(): Step {
  return { ...newStep("always", 0), unit: "hours" };
}

function describeDelay(step: Step): string {
  if (step.delayHours === 0) return "Immediately";
  if (step.delayHours % 24 === 0) {
    const days = step.delayHours / 24;
    return `Wait ${days} ${days === 1 ? "day" : "days"}`;
  }
  return `Wait ${step.delayHours} ${step.delayHours === 1 ? "hour" : "hours"}`;
}

/** Total elapsed time across the flow, for the summary line. */
function describeTotal(steps: Step[]): string {
  const hours = steps.reduce((sum, s) => sum + s.delayHours, 0);
  if (hours === 0) return "all at once";
  if (hours % 24 === 0) {
    const days = hours / 24;
    return `over ${days} ${days === 1 ? "day" : "days"}`;
  }
  return `over ${hours} ${hours === 1 ? "hour" : "hours"}`;
}

export function FlowBuilder({
  steps,
  templates,
  onChange,
  lockedCount = 0,
}: {
  steps: Step[];
  templates: TemplateOption[];
  onChange: (steps: Step[]) => void;
  /**
   * Leading steps that contacts have already been sent. Their email and their
   * position are fixed — see the API's own guard — so the controls that would
   * move them are not offered at all. Showing a disabled control the server
   * will reject is worse than showing none.
   */
  lockedCount?: number;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Without a small threshold every click on a card's select counts as a
      // drag and the field never opens.
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const patch = (uid: string, next: Partial<Step>) =>
    onChange(steps.map((s) => (s.uid === uid ? { ...s, ...next } : s)));

  const insertAt = (index: number) => {
    if (index < lockedCount) return;
    const next = [...steps];
    next.splice(index, 0, index === 0 ? firstStep() : newStep());
    // Whatever lands first must always send: there is nothing before it to
    // branch on, and the API rejects a conditional opening step.
    onChange(normalise(next));
  };

  const remove = (uid: string) => onChange(normalise(steps.filter((s) => s.uid !== uid)));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = steps.findIndex((s) => s.uid === active.id);
    const to = steps.findIndex((s) => s.uid === over.id);
    if (from < 0 || to < 0) return;
    // Neither end may be a step somebody has already been sent.
    if (from < lockedCount || to < lockedCount) return;
    const next = [...steps];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    onChange(normalise(next));
  };

  const emailCount = steps.length;

  return (
    <div className="flow">
      <div className="flow-entry">
        <span className="flow-entry-dot" />
        <div>
          <div className="flow-entry-title">Contact enters the sequence</div>
          <div className="flow-entry-sub">
            {emailCount} {emailCount === 1 ? "email" : "emails"} {describeTotal(steps)}
          </div>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={steps.map((s) => s.uid)} strategy={verticalListSortingStrategy}>
          {steps.map((step, i) => (
            <div key={step.uid}>
              <Connector
                step={step}
                index={i}
                canInsert={i >= lockedCount}
                onPatch={(next) => patch(step.uid, next)}
                onInsert={() => insertAt(i)}
              />
              <EmailNode
                step={step}
                index={i}
                templates={templates}
                locked={i < lockedCount}
                onPatch={(next) => patch(step.uid, next)}
                onRemove={steps.length > 1 && i >= lockedCount ? () => remove(step.uid) : undefined}
              />
            </div>
          ))}
        </SortableContext>
      </DndContext>

      <div className="flow-rail">
        <button type="button" className="flow-add" onClick={() => insertAt(steps.length)}>
          + Add email
        </button>
      </div>

      <div className="flow-end">Sequence ends</div>
    </div>
  );
}

/**
 * Step 0 can only ever be "always", and its delay is measured from enrollment
 * rather than from a previous send. Enforced here so a drag cannot produce a
 * flow the API will refuse.
 */
function normalise(steps: Step[]): Step[] {
  return steps.map((s, i) => (i === 0 ? { ...s, condition: "always" as const } : s));
}

function Connector({
  step,
  index,
  canInsert,
  onPatch,
  onInsert,
}: {
  step: Step;
  index: number;
  canInsert: boolean;
  onPatch: (next: Partial<Step>) => void;
  onInsert: () => void;
}) {
  const value = step.unit === "days" ? Math.round(step.delayHours / 24) : step.delayHours;

  const setValue = (raw: number) => {
    const n = Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 0;
    onPatch({ delayHours: step.unit === "days" ? n * 24 : n });
  };

  const setUnit = (unit: Step["unit"]) => {
    // Keep the number the operator is looking at and reinterpret it, rather
    // than converting: typing "3" then switching to days should mean 3 days.
    onPatch({ unit, delayHours: unit === "days" ? value * 24 : value });
  };

  return (
    <div className="flow-rail">
      {canInsert && (
        <button
          type="button"
          className="flow-insert"
          onClick={onInsert}
          aria-label={`Insert an email before step ${index + 1}`}
          title="Insert an email here"
        >
          +
        </button>
      )}

      <div className="flow-wait">
        <span className="flow-wait-label">{describeDelay(step)}</span>
        <input
          type="number"
          min={0}
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          aria-label={
            index === 0 ? "Delay before the first email" : `Delay before step ${index + 1}`
          }
        />
        <select
          value={step.unit}
          onChange={(e) => setUnit(e.target.value as Step["unit"])}
          aria-label="Delay unit"
        >
          <option value="hours">hours</option>
          <option value="days">days</option>
        </select>
        <span className="flow-wait-from">
          {index === 0 ? "after enrolling" : "after the previous email"}
        </span>
      </div>
    </div>
  );
}

function EmailNode({
  step,
  index,
  templates,
  locked,
  onPatch,
  onRemove,
}: {
  step: Step;
  index: number;
  templates: TemplateOption[];
  locked: boolean;
  onPatch: (next: Partial<Step>) => void;
  /** Absent on the last remaining step: a sequence with no emails is not one. */
  onRemove?: (() => void) | undefined;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.uid,
    disabled: locked,
  });

  const chosen = templates.find((t) => t.id === step.templateId);

  return (
    <div
      ref={setNodeRef}
      className={`flow-node${isDragging ? " dragging" : ""}${step.templateId ? "" : " unset"}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {locked ? (
        <span className="flow-handle locked" title="Already sent — this step is fixed">
          🔒
        </span>
      ) : (
        <button
          type="button"
          className="flow-handle"
          {...attributes}
          {...listeners}
          aria-label="Reorder"
        >
          ⠿
        </button>
      )}

      <div className="flow-num">{index + 1}</div>

      <div className="flow-body">
        <div className="flow-node-head">
          <span className="flow-kind">Email</span>
          {locked && <span className="flow-sent">Sent</span>}
          {index > 0 && (
            <select
              className={`flow-chip${step.condition === "always" ? "" : " on"}`}
              value={step.condition}
              onChange={(e) => onPatch({ condition: e.target.value as Condition })}
              aria-label={`Condition for step ${index + 1}`}
            >
              {Object.entries(CONDITION_LABEL).map(([v, label]) => (
                <option key={v} value={v}>
                  {CONDITION_CHIP[v as Condition]} — {label}
                </option>
              ))}
            </select>
          )}
        </div>

        <select
          className="flow-template"
          value={step.templateId}
          onChange={(e) => onPatch({ templateId: e.target.value })}
          disabled={locked}
          required
        >
          <option value="">Choose a template…</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>

        <div className="flow-subject">
          {chosen?.subject ? chosen.subject : <span className="flow-muted">No subject set</span>}
        </div>
      </div>

      {onRemove && (
        <button
          type="button"
          className="flow-remove"
          onClick={onRemove}
          aria-label={`Remove step ${index + 1}`}
        >
          ✕
        </button>
      )}
    </div>
  );
}
